import { ADMIN_DATE_PLACEHOLDER, classifyAdminCallableError } from './adminUserDisplay.js';

export { ADMIN_DATE_PLACEHOLDER, classifyAdminCallableError };

export function adminProspectPublicResponseIntentLabel(intent, language = 'fr') {
  const fr = {
    profitability_guide: 'Guide',
    property_value: 'Valeur',
    property_sale: 'Vente',
    legacy_owner_response: 'Réponse héritée',
  };
  const en = {
    profitability_guide: 'Guide',
    property_value: 'Value',
    property_sale: 'Sale',
    legacy_owner_response: 'Legacy response',
  };
  const table = language === 'en' ? en : fr;
  return table[intent] || intent;
}

export function adminProspectPublicResponseCopy(language = 'fr') {
  if (language === 'en') {
    return {
      column: 'Responses',
      panelLabel: 'Public responses',
      panelTitle: 'Public responses',
      viewDetails: 'View details',
      close: 'Close',
      loading: 'Loading…',
      error: 'Unable to load responses.',
      empty: 'No responses yet.',
      ownershipAttested: 'Ownership attested',
      submission: 'Submission',
      intent: 'Intent',
      submittedAt: 'Submitted',
      schemaVersion: 'Schema version',
      languageLabel: 'Language',
      contact: 'Contact',
      financial: 'Financial information',
      grossAnnualIncome: 'Gross annual income',
      municipalTaxes: 'Municipal taxes',
      schoolTaxes: 'School taxes',
      insurance: 'Insurance',
      heatingElectricity: 'Heating / electricity',
      otherExpenses: 'Other expenses',
      otherExpensesDescription: 'Other expenses description',
      unknown: 'Unknown',
      building: 'Building profile',
      constructionYear: 'Construction year',
      constructionYearUnknown: 'Unknown',
      declaredUnits: 'Declared unit total',
      importedUnits: 'Imported unit count',
      unitMismatch: 'Declared composition differs from imported unit count',
      unitOneAndHalf: '1½ / studio',
      unitTwoAndHalf: '2½',
      unitThreeAndHalf: '3½',
      unitFourAndHalf: '4½',
      unitFiveAndHalf: '5½',
      unitSixAndHalfPlus: '6½+',
      unitCommercial: 'Commercial',
      unitOther: 'Other',
    };
  }
  return {
    column: 'Réponses',
    panelLabel: 'Réponses publiques',
    panelTitle: 'Réponses publiques',
    viewDetails: 'Voir le détail',
    close: 'Fermer',
    loading: 'Chargement…',
    error: 'Impossible de charger les réponses.',
    empty: 'Aucune réponse pour le moment.',
    ownershipAttested: 'Propriété attestée',
    submission: 'Soumission',
    intent: 'Intention',
    submittedAt: 'Date',
    schemaVersion: 'Version du schéma',
    languageLabel: 'Langue',
    contact: 'Coordonnées',
    financial: 'Informations financières',
    grossAnnualIncome: 'Revenus bruts annuels',
    municipalTaxes: 'Taxes municipales',
    schoolTaxes: 'Taxes scolaires',
    insurance: 'Assurance',
    heatingElectricity: 'Chauffage / électricité',
    otherExpenses: 'Autres dépenses',
    otherExpensesDescription: 'Description des autres dépenses',
    unknown: 'Inconnu',
    building: 'Profil de l’immeuble',
    constructionYear: 'Année de construction',
    constructionYearUnknown: 'Inconnue',
    declaredUnits: 'Total d’unités déclaré',
    importedUnits: 'Nombre d’unités importé',
    unitMismatch: 'La composition déclarée diffère du nombre d’unités importé',
    unitOneAndHalf: '1½ / studio',
    unitTwoAndHalf: '2½',
    unitThreeAndHalf: '3½',
    unitFourAndHalf: '4½',
    unitFiveAndHalf: '5½',
    unitSixAndHalfPlus: '6½+',
    unitCommercial: 'Commerciales',
    unitOther: 'Autres',
  };
}

export function listMailingReceivedIntentBadges(publicResponse) {
  const badges = [];
  if (!publicResponse || typeof publicResponse !== 'object') return badges;
  if (publicResponse.legacyReceived === true) {
    badges.push({ intent: 'legacy_owner_response', submittedAt: null });
  }
  const received = publicResponse.receivedIntents;
  if (received && typeof received === 'object') {
    for (const intent of ['profitability_guide', 'property_value', 'property_sale']) {
      if (received[intent]?.status === 'received') {
        badges.push({
          intent,
          submittedAt: typeof received[intent].submittedAt === 'string' ? received[intent].submittedAt : null,
        });
      }
    }
  }
  return badges;
}


export const ADMIN_PROSPECT_CAMPAIGN_BUILD_POLL_MS = 2500;
/** Stop polling after this window even if still building. */
export const ADMIN_PROSPECT_CAMPAIGN_BUILD_POLL_MAX_MS = 5 * 60 * 1000;

/**
 * @param {string | null | undefined} buildStatus
 * @param {(key: string) => string} t
 */
export function adminProspectCampaignBuildStatusLabel(buildStatus, t) {
  if (buildStatus === 'building') return t('admin.prospection.campaign.build.building');
  if (buildStatus === 'ready') return t('admin.prospection.campaign.build.ready');
  if (buildStatus === 'failed') return t('admin.prospection.campaign.build.failed');
  return ADMIN_DATE_PLACEHOLDER;
}

/**
 * @param {string | null | undefined} buildStatus
 */
export function adminProspectCampaignBuildStatusBadgeClass(buildStatus) {
  if (buildStatus === 'building') return 'bg-amber-100 text-amber-950 ring-amber-200';
  if (buildStatus === 'ready') return 'bg-emerald-100 text-emerald-900 ring-emerald-200';
  if (buildStatus === 'failed') return 'bg-rose-100 text-rose-900 ring-rose-200';
  return 'bg-slate-100 text-slate-800 ring-slate-200';
}

/**
 * @param {string | null | undefined} selectionMode
 * @param {(key: string) => string} t
 */
export function adminProspectCampaignSelectionModeLabel(selectionMode, t) {
  if (selectionMode === 'explicit') return t('admin.prospection.campaign.selection.explicit');
  if (selectionMode === 'all_matching') return t('admin.prospection.campaign.selection.allMatching');
  return ADMIN_DATE_PLACEHOLDER;
}

/**
 * Safe failed-state copy. Never surfaces raw backend messages.
 * @param {string | null | undefined} failureCode
 * @param {(key: string) => string} t
 */
export function adminProspectCampaignFailureMessage(failureCode, t) {
  if (failureCode === 'count_mismatch') {
    return t('admin.prospection.campaign.failure.count_mismatch');
  }
  if (failureCode === 'readiness_count_mismatch') {
    return t('admin.prospection.campaign.failure.readiness_count_mismatch');
  }
  if (failureCode === 'async_enqueue_failed') {
    return t('admin.prospection.campaign.failure.async_enqueue_failed');
  }
  if (failureCode === 'build_failed') {
    return t('admin.prospection.campaign.failure.build_failed');
  }
  if (failureCode === 'campaign_build_retry_exhausted') {
    return t('admin.prospection.campaign.failure.campaign_build_retry_exhausted');
  }
  if (failureCode === 'mailing_build_retry_exhausted') {
    return t('admin.prospection.campaign.failure.mailing_build_retry_exhausted');
  }
  return t('admin.prospection.campaign.failure.generic');
}

/** Known mailing readiness reason codes from backend v1. */
export const ADMIN_PROSPECT_CAMPAIGN_MAILING_REASON_CODES = Object.freeze([
  'missing_owner',
  'missing_owner_name',
  'missing_mailing_address',
]);

/**
 * @param {{ buildStatus?: string | null, mailingReadinessSummary?: object | null } | null | undefined} campaign
 */
export function campaignHasMailingReadinessSummary(campaign) {
  const summary = campaign?.mailingReadinessSummary;
  return (
    campaign?.buildStatus === 'ready' &&
    summary != null &&
    typeof summary === 'object' &&
    !Array.isArray(summary)
  );
}

/**
 * @param {string | null | undefined} status
 * @param {(key: string) => string} t
 */
export function adminProspectCampaignMailingReadinessStatusLabel(status, t) {
  if (status === 'ready') return t('admin.prospection.campaign.readiness.status.ready');
  if (status === 'partial') return t('admin.prospection.campaign.readiness.status.partial');
  if (status === 'blocked') return t('admin.prospection.campaign.readiness.status.blocked');
  return ADMIN_DATE_PLACEHOLDER;
}

/**
 * @param {string | null | undefined} status
 */
export function adminProspectCampaignMailingReadinessStatusBadgeClass(status) {
  if (status === 'ready') return 'bg-emerald-100 text-emerald-900 ring-emerald-200';
  if (status === 'partial') return 'bg-amber-100 text-amber-950 ring-amber-200';
  if (status === 'blocked') return 'bg-rose-100 text-rose-900 ring-rose-200';
  return 'bg-slate-100 text-slate-800 ring-slate-200';
}

/**
 * Map known reason enum to human copy. Unknown codes use safe fallback — never raw code.
 * @param {string | null | undefined} reasonCode
 * @param {(key: string) => string} t
 */
export function adminProspectCampaignMailingReadinessReasonLabel(reasonCode, t) {
  if (reasonCode === 'missing_owner') {
    return t('admin.prospection.campaign.readiness.reason.missing_owner');
  }
  if (reasonCode === 'missing_owner_name') {
    return t('admin.prospection.campaign.readiness.reason.missing_owner_name');
  }
  if (reasonCode === 'missing_mailing_address') {
    return t('admin.prospection.campaign.readiness.reason.missing_mailing_address');
  }
  return t('admin.prospection.campaign.readiness.reason.unknown');
}

/**
 * @param {{ reasons?: unknown } | null | undefined} mailingReadiness
 * @param {(key: string) => string} t
 */
export function formatCampaignTargetMailingReadinessReasons(mailingReadiness, t) {
  const reasons = Array.isArray(mailingReadiness?.reasons) ? mailingReadiness.reasons : [];
  return reasons
    .filter((code) => typeof code === 'string' && code.trim())
    .map((code) => adminProspectCampaignMailingReadinessReasonLabel(code, t));
}

/**
 * Partial targets with multiple recipients — e.g. "1 of 2 recipients ready".
 * Returns empty string when not applicable (ready+badge enough, or single recipient).
 * @param {{ status?: string, recipientCount?: number, mailableRecipientCount?: number } | null | undefined} mailingReadiness
 * @param {(key: string) => string} t
 */
export function formatCampaignTargetMailingReadinessRecipients(mailingReadiness, t) {
  const status = mailingReadiness?.status;
  const recipientCount = Number(mailingReadiness?.recipientCount);
  const mailableRecipientCount = Number(mailingReadiness?.mailableRecipientCount);
  if (!Number.isInteger(recipientCount) || recipientCount <= 1) return '';
  if (status === 'ready' && mailableRecipientCount === recipientCount) return '';
  if (status !== 'partial' && status !== 'blocked') return '';
  const mailable = Number.isInteger(mailableRecipientCount) ? mailableRecipientCount : 0;
  return t('admin.prospection.campaign.readiness.recipientsPartial')
    .replace('{mailable}', String(mailable))
    .replace('{total}', String(recipientCount));
}

/**
 * @param {string | null | undefined} status
 */
export function campaignTargetNeedsProspectFixAction(status) {
  return status === 'partial' || status === 'blocked';
}

/**
 * @param {{ letterTemplate?: { templateId?: string | null, templateVersion?: number | null, templateName?: string | null } | null | undefined }} campaign
 * @param {(key: string) => string} t
 */
export function formatCampaignLetterTemplateLabel(campaign, t) {
  const ref = campaign?.letterTemplate;
  if (!ref || typeof ref !== 'object') return null;
  const name =
    typeof ref.templateName === 'string' && ref.templateName.trim()
      ? ref.templateName.trim()
      : null;
  const version = Number.isInteger(ref.templateVersion) ? ref.templateVersion : null;
  if (!name && version == null) return null;
  if (name && version != null) {
    return t('admin.prospection.campaign.letterTemplate.frozen')
      .replace('{name}', name)
      .replace('{version}', String(version));
  }
  if (name) return name;
  return t('admin.prospection.campaign.letterTemplate.versionOnly').replace(
    '{version}',
    String(version),
  );
}

/**
 * @param {string | null | undefined} kind
 */
export function adminProspectCampaignErrorKey(kind) {
  if (kind === 'unauthenticated') return 'admin.prospection.campaign.error.unauthenticated';
  if (kind === 'permission-denied') return 'admin.prospection.campaign.error.permission';
  if (kind === 'invalid-argument') return 'admin.prospection.campaign.error.invalid';
  if (kind === 'not-found') return 'admin.prospection.campaign.error.notFound';
  if (kind === 'already-exists') return 'admin.prospection.campaign.error.alreadyExists';
  if (kind === 'failed-precondition') return 'admin.prospection.campaign.error.failedPrecondition';
  if (kind === 'unavailable') return 'admin.prospection.campaign.error.unavailable';
  if (kind === 'resource-exhausted') return 'admin.prospection.campaign.error.resourceExhausted';
  return 'admin.prospection.campaign.error';
}

/**
 * Snapshot property line helpers — never pull live Prospect 360 fields.
 * @param {{ property?: { address?: string | null } } | null | undefined} target
 */
export function formatCampaignTargetAddress(target) {
  const address = target?.property?.address;
  return typeof address === 'string' ? address.trim() : '';
}

/**
 * @param {{ property?: { city?: string | null, postalCode?: string | null } } | null | undefined} target
 */
export function formatCampaignTargetLocality(target) {
  const parts = [target?.property?.city, target?.property?.postalCode]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean);
  return parts.join(' ');
}

/**
 * @param {{ property?: { numberOfUnits?: unknown } } | null | undefined} target
 */
export function formatCampaignTargetUnits(target) {
  const value = target?.property?.numberOfUnits;
  if (value == null || value === '') return ADMIN_DATE_PLACEHOLDER;
  const n = Number(value);
  if (!Number.isFinite(n)) return ADMIN_DATE_PLACEHOLDER;
  return String(n);
}

/**
 * @param {{ owner?: { name?: string | null, mailingAddress?: string | null } | null } | null | undefined} target
 */
export function formatCampaignTargetOwnerName(target) {
  const name = target?.owner?.name;
  return typeof name === 'string' && name.trim() ? name.trim() : ADMIN_DATE_PLACEHOLDER;
}

/**
 * @param {{ owner?: { name?: string | null, mailingAddress?: string | null } | null } | null | undefined} target
 */
export function formatCampaignTargetOwnerMailing(target) {
  const mailing = target?.owner?.mailingAddress;
  return typeof mailing === 'string' && mailing.trim() ? mailing.trim() : ADMIN_DATE_PLACEHOLDER;
}

export const ADMIN_PROSPECT_LETTER_VERSION_REAL_QR = 3;
export const ADMIN_PROSPECT_LETTER_VERSION_DYNAMIC_REAL_QR = 4;
export const ADMIN_PROSPECT_CAMPAIGN_MAILINGS_PAGE_SIZE = 25;
export const ADMIN_PROSPECT_CAMPAIGN_LETTER_BUILD_POLL_MS = ADMIN_PROSPECT_CAMPAIGN_BUILD_POLL_MS;
export const ADMIN_PROSPECT_CAMPAIGN_LETTER_BUILD_POLL_MAX_MS = ADMIN_PROSPECT_CAMPAIGN_BUILD_POLL_MAX_MS;
export const ADMIN_PROSPECT_CAMPAIGN_MAILING_BUILD_POLL_MS = ADMIN_PROSPECT_CAMPAIGN_BUILD_POLL_MS;
export const ADMIN_PROSPECT_CAMPAIGN_MAILING_BUILD_POLL_MAX_MS = ADMIN_PROSPECT_CAMPAIGN_BUILD_POLL_MAX_MS;

/**
 * @param {{
 *   buildStatus?: string | null,
 *   mailingReadinessSummary?: { mailableRecipientCount?: number | null } | null,
 *   mailingBuildStatus?: string | null,
 *   mailingBuildRecoverable?: boolean | null,
 * } | null | undefined} campaign
 */
export function canPrepareAdminProspectCampaignMailings(campaign) {
  if (!campaignHasMailingReadinessSummary(campaign)) return false;
  const mailable = Number(campaign.mailingReadinessSummary?.mailableRecipientCount);
  if (!Number.isInteger(mailable) || mailable <= 0) return false;
  const mailingBuildStatus = campaign.mailingBuildStatus || 'not_started';
  if (mailingBuildStatus === 'not_started') return true;
  if (mailingBuildStatus === 'failed' && campaign.mailingBuildRecoverable === true) return true;
  return false;
}

/**
 * @param {{ buildRecoverable?: boolean | null } | null | undefined} campaign
 */
export function canRetryAdminProspectCampaignBuild(campaign) {
  return campaign?.buildRecoverable === true;
}

export function canRetryAdminProspectCampaignMailingBuild(campaign) {
  return campaign?.mailingBuildRecoverable === true;
}

/**
 * Zero mailable recipients after a ready campaign with readiness summary.
 * @param {{
 *   buildStatus?: string | null,
 *   mailingReadinessSummary?: { mailableRecipientCount?: number | null } | null,
 * } | null | undefined} campaign
 */
export function campaignHasZeroMailableRecipients(campaign) {
  if (!campaignHasMailingReadinessSummary(campaign)) return false;
  const mailable = Number(campaign.mailingReadinessSummary?.mailableRecipientCount);
  return Number.isInteger(mailable) && mailable <= 0;
}

/**
 * @param {string | null | undefined} mailingBuildStatus
 * @param {(key: string) => string} t
 */
export function adminProspectCampaignMailingBuildStatusLabel(mailingBuildStatus, t) {
  if (mailingBuildStatus === 'building') return t('admin.prospection.campaign.mailings.status.building');
  if (mailingBuildStatus === 'ready') return t('admin.prospection.campaign.mailings.status.ready');
  if (mailingBuildStatus === 'failed') return t('admin.prospection.campaign.mailings.status.failed');
  if (mailingBuildStatus === 'not_started') return t('admin.prospection.campaign.mailings.status.not_started');
  return ADMIN_DATE_PLACEHOLDER;
}

/**
 * @param {string | null | undefined} mailingBuildStatus
 */
export function adminProspectCampaignMailingBuildStatusBadgeClass(mailingBuildStatus) {
  if (mailingBuildStatus === 'building') return 'bg-amber-100 text-amber-950 ring-amber-200';
  if (mailingBuildStatus === 'ready') return 'bg-emerald-100 text-emerald-900 ring-emerald-200';
  if (mailingBuildStatus === 'failed') return 'bg-rose-100 text-rose-900 ring-rose-200';
  return 'bg-slate-100 text-slate-800 ring-slate-200';
}

export const ADMIN_PROSPECT_CAMPAIGN_LETTER_ERROR_REASONS = Object.freeze([
  'mailings_not_ready',
  'no_mailings',
  'campaign_not_ready',
  'no_mailable_recipients',
  'letter_not_generated',
  'letter_failed',
  'letter_stale',
  'letter_artifact_invalid',
  'letter_artifact_missing',
  'letter_storage_mismatch',
]);

/**
 * @param {{ letterTemplate?: unknown } | null | undefined} campaign
 */
export function expectedCurrentLetterVersionForCampaign(campaign) {
  return campaign?.letterTemplate ? ADMIN_PROSPECT_LETTER_VERSION_DYNAMIC_REAL_QR : ADMIN_PROSPECT_LETTER_VERSION_REAL_QR;
}

/**
 * @param {{ letterVersion?: unknown, letterTemplate?: unknown } | null | undefined} campaign
 */
export function campaignHasCurrentLetterVersion(campaign) {
  return Number(campaign?.letterVersion) === expectedCurrentLetterVersionForCampaign(campaign);
}

/**
 * @param {{ buildStatus?: string | null } | null | undefined} campaign
 */
export function campaignLettersSectionVisible(campaign) {
  return campaign?.buildStatus === 'ready';
}

/**
 * @param {{
 *   buildStatus?: string | null,
 *   mailingBuildStatus?: string | null,
 *   mailingCount?: number | null,
 *   letterBuildStatus?: string | null,
 *   letterVersion?: number | null,
 *   letterTemplate?: unknown,
 * } | null | undefined} campaign
 */
export function canGenerateAdminProspectCampaignLetters(campaign) {
  if (!campaign || campaign.buildStatus !== 'ready') return false;
  if (campaign.mailingBuildStatus !== 'ready') return false;
  const mailingCount = Number(campaign.mailingCount);
  if (!Number.isInteger(mailingCount) || mailingCount <= 0) return false;
  if (campaign.letterBuildStatus === 'building') return false;
  if (campaign.letterBuildStatus === 'ready' && campaignHasCurrentLetterVersion(campaign)) return false;
  return true;
}

/**
 * @param {string | null | undefined} letterBuildStatus
 * @param {(key: string) => string} t
 */
export function adminProspectCampaignLetterBuildStatusLabel(letterBuildStatus, t) {
  if (letterBuildStatus === 'building') return t('admin.prospection.campaign.letters.status.building');
  if (letterBuildStatus === 'ready') return t('admin.prospection.campaign.letters.status.ready');
  if (letterBuildStatus === 'failed') return t('admin.prospection.campaign.letters.status.failed');
  if (letterBuildStatus === 'not_started') return t('admin.prospection.campaign.letters.status.not_started');
  return ADMIN_DATE_PLACEHOLDER;
}

/**
 * @param {string | null | undefined} letterBuildStatus
 */
export function adminProspectCampaignLetterBuildStatusBadgeClass(letterBuildStatus) {
  if (letterBuildStatus === 'building') return 'bg-amber-100 text-amber-950 ring-amber-200';
  if (letterBuildStatus === 'ready') return 'bg-emerald-100 text-emerald-900 ring-emerald-200';
  if (letterBuildStatus === 'failed') return 'bg-rose-100 text-rose-900 ring-rose-200';
  return 'bg-slate-100 text-slate-800 ring-slate-200';
}

/**
 * @param {string | null | undefined} letterStatus
 * @param {(key: string) => string} t
 */
export function adminProspectCampaignMailingLetterStatusLabel(letterStatus, t) {
  if (letterStatus === 'generating') return t('admin.prospection.campaign.letters.mailing.generating');
  if (letterStatus === 'generated') return t('admin.prospection.campaign.letters.mailing.generated');
  if (letterStatus === 'failed') return t('admin.prospection.campaign.letters.mailing.failed');
  if (letterStatus === 'not_generated') return t('admin.prospection.campaign.letters.mailing.not_generated');
  return ADMIN_DATE_PLACEHOLDER;
}

/**
 * @param {{ letterStatus?: string | null, letterArtifact?: object | null, kind?: string } | null | undefined} mailing
 * @param {{ letterBuildStatus?: string | null, letterTemplate?: unknown, letterVersion?: number | null } | null | undefined} campaign
 */
export function isCampaignLetterDownloadable(mailing, campaign) {
  if (!mailing || mailing.kind === 'blocked') return false;
  if (campaign?.letterBuildStatus === 'building') return false;
  if (mailing.letterStatus !== 'generated') return false;
  const artifact = mailing.letterArtifact;
  if (!artifact || typeof artifact !== 'object' || Array.isArray(artifact)) return false;
  if (artifact.version !== expectedCurrentLetterVersionForCampaign(campaign)) return false;
  if (!Number.isInteger(artifact.byteSize) || artifact.byteSize <= 0) return false;
  if (!artifact.generatedAt) return false;
  return true;
}

/**
 * @param {{ mailings?: object[], targets?: object[] }} params
 */
export function buildCampaignPdfLetterRows({ mailings, targets } = {}) {
  const mailingRows = (Array.isArray(mailings) ? mailings : []).map((mailing) => ({
    key: mailing.mailingId,
    kind: 'mailing',
    mailingId: mailing.mailingId,
    targetId: mailing.targetId || null,
    prospectId: mailing.prospectId || mailing.targetId || null,
    targetSequence: Number.isInteger(mailing.targetSequence) ? mailing.targetSequence : 0,
    recipientOrdinal: Number.isInteger(mailing.recipientOrdinal) ? mailing.recipientOrdinal : 0,
    letterStatus: mailing.letterStatus || 'not_generated',
    letterArtifact: mailing.letterArtifact || null,
    recipient: mailing.recipient || null,
    property: mailing.property || null,
    publicResponse: mailing.publicResponse || null,
  }));
  const mailingTargetIds = new Set(mailingRows.map((row) => row.targetId).filter(Boolean));
  const mailingProspectIds = new Set(mailingRows.map((row) => row.prospectId).filter(Boolean));
  const blockedRows = (Array.isArray(targets) ? targets : [])
    .filter((target) => target?.mailingReadiness?.status === 'blocked')
    .filter((target) => {
      const targetId = target.targetId || target.prospectId;
      const prospectId = target.prospectId || target.targetId;
      if (targetId && mailingTargetIds.has(targetId)) return false;
      if (prospectId && mailingProspectIds.has(prospectId)) return false;
      return true;
    })
    .map((target) => ({
      key: `blocked:${target.targetId || target.prospectId}`,
      kind: 'blocked',
      mailingId: null,
      targetId: target.targetId || null,
      prospectId: target.prospectId || target.targetId || null,
      targetSequence: Number.isInteger(target.sequence) ? target.sequence : 0,
      recipientOrdinal: 0,
      letterStatus: null,
      letterArtifact: null,
      recipient: target.owner || null,
      property: target.property || null,
    }));
  return [...mailingRows, ...blockedRows];
}

/**
 * @param {object[] | null | undefined} mailings
 */
export function countMailingLetterStatuses(mailings) {
  let generated = 0;
  let failed = 0;
  let pending = 0;
  for (const mailing of Array.isArray(mailings) ? mailings : []) {
    if (mailing.letterStatus === 'generated') generated += 1;
    else if (mailing.letterStatus === 'failed') failed += 1;
    else pending += 1;
  }
  return { generated, failed, pending };
}

/**
 * @param {unknown} error
 * @returns {string | null}
 */
export function extractCampaignLetterErrorReason(error) {
  const detailsReason = error?.details?.reason;
  const message = String(error?.message || '').trim();
  const candidates = [detailsReason, message];
  for (const value of candidates) {
    if (typeof value === 'string' && ADMIN_PROSPECT_CAMPAIGN_LETTER_ERROR_REASONS.includes(value)) {
      return value;
    }
  }
  return null;
}

/**
 * @param {unknown} error
 */
export function adminProspectCampaignLetterErrorKey(error) {
  const reason = extractCampaignLetterErrorReason(error);
  if (reason === 'mailings_not_ready' || reason === 'campaign_not_ready') {
    return 'admin.prospection.campaign.letters.notReady';
  }
  if (reason === 'no_mailings' || reason === 'no_mailable_recipients') {
    return 'admin.prospection.campaign.letters.noMailable';
  }
  if (
    reason === 'letter_stale' ||
    reason === 'letter_artifact_invalid' ||
    reason === 'letter_storage_mismatch'
  ) {
    return 'admin.prospection.campaign.letters.stale';
  }
  if (reason === 'letter_artifact_missing') {
    return 'admin.prospection.campaign.letters.missingObject';
  }
  if (reason === 'letter_not_generated' || reason === 'letter_failed') {
    return 'admin.prospection.campaign.letters.downloadError';
  }
  const kind = classifyAdminCallableError(error);
  if (kind === 'unauthenticated') return 'admin.prospection.campaign.error.unauthenticated';
  if (kind === 'permission-denied') return 'admin.prospection.campaign.error.permission';
  if (kind === 'unavailable') return 'admin.prospection.campaign.error.unavailable';
  if (kind === 'not-found') return 'admin.prospection.campaign.letters.downloadError';
  if (kind === 'failed-precondition') return 'admin.prospection.campaign.letters.notReady';
  return 'admin.prospection.campaign.letters.error';
}

/**
 * @param {{ name?: string | null, mailingAddress?: string | null } | null | undefined} recipient
 */
export function formatCampaignLetterRecipientName(recipient) {
  const name = recipient?.name;
  return typeof name === 'string' && name.trim() ? name.trim() : ADMIN_DATE_PLACEHOLDER;
}

/**
 * @param {{ mailingAddress?: string | null } | null | undefined} recipient
 */
export function formatCampaignLetterRecipientMailing(recipient) {
  const mailing = recipient?.mailingAddress;
  return typeof mailing === 'string' && mailing.trim() ? mailing.trim() : ADMIN_DATE_PLACEHOLDER;
}

/** Phase 6 — bulk campaign letter ZIP download. */
export const ADMIN_PROSPECT_CAMPAIGN_LETTER_ARCHIVE_BUILD_POLL_MS = ADMIN_PROSPECT_CAMPAIGN_BUILD_POLL_MS;
export const ADMIN_PROSPECT_CAMPAIGN_LETTER_ARCHIVE_BUILD_POLL_MAX_MS =
  ADMIN_PROSPECT_CAMPAIGN_BUILD_POLL_MAX_MS;

export const ADMIN_PROSPECT_CAMPAIGN_LETTER_ARCHIVE_ERROR_REASONS = Object.freeze([
  'letters_not_ready',
  'archive_not_ready',
  'archive_failed',
  'archive_stale',
  'archive_artifact_missing',
]);

/**
 * Every stage this feature depends on is ready, the letter version is current,
 * and the generated/expected letter counts match and are both > 0. Field names
 * mirror the backend exactly (buildStatus / mailingBuildStatus / letterBuildStatus).
 * @param {{
 *   buildStatus?: string | null,
 *   mailingBuildStatus?: string | null,
 *   letterBuildStatus?: string | null,
 *   letterCountExpected?: number | null,
 *   letterCountGenerated?: number | null,
 * } | null | undefined} campaign
 */
export function campaignLettersCompletelyGenerated(campaign) {
  if (!campaign) return false;
  if (campaign.buildStatus !== 'ready') return false;
  if (campaign.mailingBuildStatus !== 'ready') return false;
  if (campaign.letterBuildStatus !== 'ready') return false;
  if (!campaignHasCurrentLetterVersion(campaign)) return false;
  const expected = Number(campaign.letterCountExpected);
  const generated = Number(campaign.letterCountGenerated);
  if (!Number.isInteger(expected) || expected <= 0) return false;
  if (!Number.isInteger(generated) || generated <= 0) return false;
  return expected === generated;
}

/**
 * @param {{
 *   letterArchiveStatus?: string | null,
 * } | null | undefined} campaign
 */
export function canRequestAdminProspectCampaignLetterArchive(campaign) {
  if (!campaignLettersCompletelyGenerated(campaign)) return false;
  const status = campaign?.letterArchiveStatus || 'not_started';
  // Failed archives must go through backend-authorized recovery (letterArchiveRecoverable),
  // never a fresh request that would reset recoveryAttemptCount.
  return status === 'not_started' || status === 'ready';
}

/**
 * @param {{ letterArchiveRecoverable?: boolean | null } | null | undefined} campaign
 */
export function canRetryAdminProspectCampaignLetterArchive(campaign) {
  return campaign?.letterArchiveRecoverable === true;
}

/**
 * @param {string | null | undefined} letterArchiveStatus
 * @param {(key: string) => string} t
 */
export function adminProspectCampaignLetterArchiveStatusLabel(letterArchiveStatus, t) {
  if (letterArchiveStatus === 'building') return t('admin.prospection.campaign.letters.archive.status.building');
  if (letterArchiveStatus === 'ready') return t('admin.prospection.campaign.letters.archive.status.ready');
  if (letterArchiveStatus === 'failed') return t('admin.prospection.campaign.letters.archive.status.failed');
  return t('admin.prospection.campaign.letters.archive.status.not_started');
}

/**
 * @param {string | null | undefined} letterArchiveStatus
 */
export function adminProspectCampaignLetterArchiveStatusBadgeClass(letterArchiveStatus) {
  if (letterArchiveStatus === 'building') return 'bg-amber-100 text-amber-950 ring-amber-200';
  if (letterArchiveStatus === 'ready') return 'bg-emerald-100 text-emerald-900 ring-emerald-200';
  if (letterArchiveStatus === 'failed') return 'bg-rose-100 text-rose-900 ring-rose-200';
  return 'bg-slate-100 text-slate-800 ring-slate-200';
}

/**
 * @param {{ letterArchiveStatus?: string | null } | null | undefined} campaign
 */
export function isCampaignLetterArchiveDownloadable(campaign) {
  return campaign?.letterArchiveStatus === 'ready';
}

/**
 * @param {unknown} error
 * @returns {string | null}
 */
export function extractCampaignLetterArchiveErrorReason(error) {
  const detailsReason = error?.details?.reason;
  const message = String(error?.message || '').trim();
  for (const value of [detailsReason, message]) {
    if (typeof value === 'string' && ADMIN_PROSPECT_CAMPAIGN_LETTER_ARCHIVE_ERROR_REASONS.includes(value)) {
      return value;
    }
  }
  return null;
}

/**
 * @param {unknown} error
 */
export function adminProspectCampaignLetterArchiveErrorKey(error) {
  const reason = extractCampaignLetterArchiveErrorReason(error);
  if (reason === 'letters_not_ready') return 'admin.prospection.campaign.letters.archive.notReady';
  if (reason === 'archive_stale') return 'admin.prospection.campaign.letters.archive.stale';
  if (reason === 'archive_artifact_missing' || reason === 'archive_failed') {
    return 'admin.prospection.campaign.letters.archive.downloadError';
  }
  if (reason === 'archive_not_ready') return 'admin.prospection.campaign.letters.archive.notReady';
  const kind = classifyAdminCallableError(error);
  if (kind === 'unauthenticated') return 'admin.prospection.campaign.error.unauthenticated';
  if (kind === 'permission-denied') return 'admin.prospection.campaign.error.permission';
  if (kind === 'unavailable') return 'admin.prospection.campaign.error.unavailable';
  if (kind === 'not-found') return 'admin.prospection.campaign.letters.archive.downloadError';
  if (kind === 'failed-precondition') return 'admin.prospection.campaign.letters.archive.notReady';
  return 'admin.prospection.campaign.letters.archive.error';
}
